import { useEffect, useState } from 'react';
import { AccordionSummary, Box, IconButton, Typography } from '@mui/material';
import { ExpandMore, Link } from '@mui/icons-material';

import { useTreatmentPlanEditable } from '../../hooks/useTreatmentPlanEditable';
import { capitalizeFirstLetter } from '../../../../../helpers';
import { FORTA_BLUE } from '../../../../../utils/colors';
import StatusTransitionIcon from './components/statusTransitionIcon';
import { GOAL_DETAILS_FIELD_IDS, iconButtonStyle, TargetStages } from '../common/constants/general';
import { useInitialValue } from './hooks';
import EditOutlinedIcon from '../../../../svgs/EditOutlinedIcon';
import TrashIcon from '../../../../svgs/TrashIcon';

import type { ITreatmentPlanGoal } from '../../../../../utils/types';

interface GoalSummaryProps {
  index: number;
  goal: ITreatmentPlanGoal;
  submitting: boolean;
  onUpdateField: (key: string, value: any) => void;
  onDelete: () => void;
}

function GoalSummary(props: GoalSummaryProps) {
  const { index, goal, submitting, onUpdateField, onDelete } = props;
  const isTpEditable = useTreatmentPlanEditable();
  const [linkedGoalNames, setLinkedGoalNames] = useState<string | null>(null);
  const initialIsActive = useInitialValue(goal?.is_active ?? true, goal?.id);

  useEffect(() => {
    if (!goal) return;

    const linkedGoals = [];
    if (goal.linked_goals?.behavior_reduction) {
      linkedGoals.push(...goal.linked_goals.behavior_reduction);
    }
    if (goal.linked_goals?.skill_acquisition) {
      linkedGoals.push(...goal.linked_goals.skill_acquisition);
    }
    if (linkedGoals && linkedGoals.length >= 0) {
      setLinkedGoalNames(
        linkedGoals
          .map((linkedGoal) =>
            capitalizeFirstLetter(linkedGoal[GOAL_DETAILS_FIELD_IDS.GOAL_NAME]?.label ?? ''),
          )
          .join(', '),
      );
    }
  }, [goal]);

  return (
    <AccordionSummary
      expandIcon={
        <ExpandMore data-testid={`expand_${goal?.[GOAL_DETAILS_FIELD_IDS.GOAL_NAME]?.label}`} />
      }
      sx={{
        padding: '16px 24px',
        '&.Mui-focusVisible': {
          backgroundColor: 'transparent',
        },
        '& .MuiAccordionSummary-content': {
          display: 'flex',
          flexDirection: 'column',
        },
        '& .MuiAccordionSummary-expandIconWrapper': {
          position: 'absolute',
          right: 10,
          top: linkedGoalNames ? 30 : 24,
        },
      }}
      onClick={() => onUpdateField('expanded', !goal?.expanded)}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Box
          sx={{ display: 'flex', flexGrow: 1, flexDirection: 'column', justifyContent: 'center' }}
        >
          <Box sx={{ display: 'flex' }}>
            <Typography
              variant="h4"
              color={FORTA_BLUE.primary}
              fontWeight={500}
              lineHeight={'34px'}
              height={34}
              mr={3}
            >
              Goal {index + 1}:{' '}
              {capitalizeFirstLetter(goal?.[GOAL_DETAILS_FIELD_IDS.GOAL_NAME]?.label || '')}
            </Typography>
            {!goal?.is_editing && (
              <Box sx={{ display: 'flex', gap: 4, mr: 1 }}>
                <StatusTransitionIcon
                  currentIsActive={goal?.is_active}
                  originalIsActive={goal?.id ? initialIsActive : undefined}
                  tooltip={goal?.is_active ? 'Goal is active' : 'Goal is inactive'}
                />
              </Box>
            )}
          </Box>
          {!goal?.is_editing && linkedGoalNames && (
            <Box sx={{ display: 'flex' }}>
              <Link sx={{ mr: 1 }} />
              <Typography textAlign={'left'}>{linkedGoalNames}</Typography>
            </Box>
          )}
          {goal?.is_editing && goal.id && goal.targets.length > 0 && (
            <Typography textAlign={'left'}>
              Target status:{' '}
              {(() => {
                const stages = Object.values(TargetStages)
                  .map((stage) => {
                    const count = goal.targets.filter(
                      (t) =>
                        t?.stage_change_logs?.[t.stage_change_logs.length - 1]?.new_stage === stage,
                    ).length;
                    return count > 0 ? `${count} ${stage}` : null;
                  })
                  .filter(Boolean);

                return stages.length > 0
                  ? `${stages.reduce((acc, curr, idx, arr) => {
                      if (idx === 0) return curr;
                      if (idx === arr.length - 1) return `${acc} and ${curr}`;
                      return `${acc}, ${curr}`;
                    })} out of ${goal.targets.length} targets`
                  : 'No targets available';
              })()}
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', mr: 3 }}>
          <IconButton
            data-testid={`delete_${goal?.[GOAL_DETAILS_FIELD_IDS.GOAL_NAME]?.label}`}
            sx={iconButtonStyle}
            hidden={!isTpEditable}
            disabled={submitting}
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
          >
            <TrashIcon color={FORTA_BLUE.primary} />
          </IconButton>
          <IconButton
            data-testid={`edit_${goal?.[GOAL_DETAILS_FIELD_IDS.GOAL_NAME]?.label}`}
            sx={{ ...iconButtonStyle, opacity: goal?.is_editing ? 0.3 : 1 }}
            hidden={!isTpEditable}
            disabled={submitting}
            onClick={(e) => {
              e.stopPropagation();
              onUpdateField('is_editing', !goal?.is_editing);
            }}
          >
            <EditOutlinedIcon color={FORTA_BLUE.primary} />
          </IconButton>
        </Box>
      </Box>
    </AccordionSummary>
  );
}

export default GoalSummary;
